import { TextField } from '@mui/material'
import React from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { LoadingButton } from 'components/common/LoadingButton'
import {
  type AzureAuthenticationFormValuesNew,
  getAzureAuthenticationFormInitializersNew
} from 'components/common/mapped-entities/azure/authentication/azure-auth-form-utils'
import { getAzureAuthenticationPayload } from 'components/common/mapped-entities/azure/authentication/azure-integration-utils'
import { AzureAuthenticationSection } from 'components/common/mapped-entities/azure/authentication/AzureAuthenticationSection'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { errorToast } from 'lib/toast'
import { useCreateAuthentication } from 'queries/external-integrations/use-create-authentication'
import { type TeamExternalIntegrationAzure } from 'types/api/status-checks/status-check'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { messageFromResponseError } from 'utils/strings'
import { type OnSuccessExternalIntegration } from 'utils/team-external-integrations/team-external-integrations-util'

export function AzureNewAuthenticationForm({
  azureExternalIntegrations,
  onSuccess,
  templateUrl
}: {
  azureExternalIntegrations: TeamExternalIntegrationAzure[]
  onSuccess?: OnSuccessExternalIntegration
  templateUrl: string
}) {
  const { reFetchExternalIntegrations } =
    useStatusCheckExternalIntegrationsContext()

  const {
    isLoading: isLoadingCreateAuthentication,
    mutateAsync: createAuthentication
  } = useCreateAuthentication()

  const authForm = useForm<AzureAuthenticationFormValuesNew>({
    ...getAzureAuthenticationFormInitializersNew(azureExternalIntegrations),
    mode: 'all'
  })

  const { control, formState, getValues } = authForm
  const { isSubmitting, isValid } = formState

  const handleSubmitAuthentication = async () => {
    try {
      const { subscriptionId } = getValues()
      const body = getAzureAuthenticationPayload(subscriptionId)
      const response = await createAuthentication(body)
      onSuccess?.(response)
      await handleDataFetchOnChange(reFetchExternalIntegrations)
    } catch (error) {
      errorToast(
        messageFromResponseError(
          error,
          'Failed to create this authentication. Please try again later'
        )
      )
    }
  }

  const isSubmitDisabled = !isValid
  const isSubmitLoading = isLoadingCreateAuthentication || isSubmitting

  return (
    <FormProvider {...authForm}>
      <AzureAuthenticationSection templateUrl={templateUrl}>
        <Controller
          control={control}
          name="subscriptionId"
          render={({ field, fieldState: { error } }) => (
            <TextField
              error={Boolean(error?.message)}
              helperText={error?.message}
              label="Subscription ID"
              sx={{ width: '100%', maxWidth: 500, marginTop: 1 }}
              {...field}
            />
          )}
        />
      </AzureAuthenticationSection>
      <LoadingButton
        isDisabled={isSubmitDisabled}
        isLoading={isSubmitLoading}
        sx={{ marginTop: 1 }}
        variant="contained"
        onClick={handleSubmitAuthentication}
      >
        {isSubmitLoading ? 'Saving' : 'Save'}
      </LoadingButton>
    </FormProvider>
  )
}
